class KlaviyoBackInStockSubscription extends HTMLElement {
  constructor() {
    super();
    this.subscriptionURL =
      "https://a.klaviyo.com/client/back-in-stock-subscriptions?company_id=HgZ6L3";
  }
  /**
   * Sets up dialog elements and event listeners when the element is added to the DOM.
   */
  connectedCallback() {
    this.dialog = this.querySelector("dialog");
    this.closeBtn = this.querySelector(
      "#close-klaviyo-back-in-stock-subscription"
    );
    this.form = this.querySelector("form");
    this.label = this.form.querySelector("label");
    this.variantInput = this.form.querySelector("input");
    this.emailInput = this.form.querySelector(`input[type="email"]`);
    this.submitBtn = this.form.querySelector("button[type='submit']");
    this.formErrorMessage = this.form.querySelector(
      "#klaviyo-back-in-stock-subscription--error"
    );

    this.formSection = this.querySelector(
      "#klaviyo-back-in-stock-subscription--form"
    );
    this.successSection = this.querySelector(
      "#klaviyo-back-in-stock-subscription--success"
    );

    this.form.addEventListener(
      "submit",
      this.formSubmitEventHandler.bind(this)
    );
    this.closeBtn.addEventListener("click", this.closeDialog.bind(this));
    document.addEventListener(
      "pdp-klaviyo-subscription",
      this.pdpKlaviyoSubscriptionEventHandler.bind(this)
    );
  }

  /**
   * Cleans up event listeners when the element is removed from the DOM.
   */
  disconnectedCallback() {
    this.closeBtn.removeEventListener("click", this.closeDialog.bind(this));
  }

  /**
   * Handles form submission by validating input and initiating the subscription request.
   * @param {SubmitEvent} e - Form submit event.
   */
  formSubmitEventHandler(e) {
    e.preventDefault();
    if (!this.form.checkValidity()) {
      this.form.reportValidity();
      return;
    }
    this.subscribe();
  }

  /**
   * Posts the subscription to Klaviyo and toggles loading, error, and success states.
   */
  async subscribe() {
    const data = this.getSubscriptionData();
    const options = {
      method: "POST",
      headers: {
        accept: "application/vnd.api+json",
        revision: "2025-10-15",
        "content-type": "application/vnd.api+json",
      },
      body: JSON.stringify(data),
    };

    this.toggleButtonLoadingStatus(true);
    this.toggleInputDisabled(this.submitBtn, true);
    this.setErrorMessage();

    try {
      const request = await fetch(this.subscriptionURL, options);
      if (request.ok && request.status === 202) {
        this.toggleSectionDisplay(this.formSection, false);
        this.toggleSectionDisplay(this.successSection, true);
      }
    } catch (error) {
      console.log(`PDP Klaviyo Subscription | ${error}`);
      this.setErrorMessage(error);
      this.toggleSectionDisplay(this.formSection, true);
      this.toggleSectionDisplay(this.successSection, false);
    } finally {
      this.toggleButtonLoadingStatus(false);
      this.toggleInputDisabled(this.submitBtn, false);
      this.form.reset();
    }
  }

  /**
   * Handles product page subscription events by updating the dialog content and displaying it.
   * @param {CustomEvent<{title: string, size: string, variantId: string}>} event - Event with product info.
   */
  pdpKlaviyoSubscriptionEventHandler(event) {
    const { title, size, variantId } = event.detail;
    this.setLabelMessage(title, size);
    this.setVariant(variantId);
    this.toggleSectionDisplay(this.successSection, false);
    this.toggleSectionDisplay(this.formSection, true);
    this.openDialog();
  }
  /**
   * Updates the dialog label with the selected product title and size.
   * @param {string} title - Product title.
   * @param {string} size - Selected size.
   */
  setLabelMessage(title, size) {
    this.label.innerHTML = `We'll send you an email when the <b>${title}</b> in <b>size: ${size}</b> is back in stock`;
  }

  /**
   * Stores the current variant ID in the hidden form input.
   * @param {string|number} variantId - Variant identifier.
   */
  setVariant(variantId) {
    this.variantInput.value = variantId;
  }

  /**
   * Shows or hides the form error message based on provided text.
   * @param {string} [error=""] - Message to display; hides the element when empty.
   */
  setErrorMessage(error = "") {
    if (error === "") {
      this.formErrorMessage.style.display = "none";
      return;
    }
    this.formErrorMessage.innerHTML = error;
    this.formErrorMessage.style.display = "block";
  }

  /**
   * Toggles visibility for a section element.
   * @param {HTMLElement} section - Element whose display should change.
   * @param {boolean} [showSection=false] - Show when true; hide when false.
   */
  toggleSectionDisplay(section, showSection = false) {
    section.style.display = showSection ? "block" : "none";
  }

  /**
   * Adds or removes the loading class on the submit button.
   * @param {boolean} [isLoading=false] - When true adds the loading state; removes it otherwise.
   */
  toggleButtonLoadingStatus(isLoading = false) {
    this.submitBtn.classList[isLoading ? "add" : "remove"]("loading-status");
  }

  /**
   * Enables or disables an input element while keeping aria-disabled in sync.
   * @param {HTMLInputElement | HTMLButtonElement} input - Target element to toggle.
   * @param {boolean} [isDisabled=false] - When true disables the input; enables otherwise.
   */
  toggleInputDisabled(input, isDisabled = false) {
    input.disabled = isDisabled;
    input.ariaDisabled = isDisabled;
  }

  /**
   * Builds the request payload for the Klaviyo back-in-stock subscription.
   * @returns {object} JSON:API payload containing profile, channel, and variant data.
   */
  getSubscriptionData() {
    return {
      data: {
        type: "back-in-stock-subscription",
        attributes: {
          profile: {
            data: {
              type: "profile",
              attributes: {
                email: this.emailInput.value,
              },
            },
          },
          channels: ["EMAIL"],
        },
        relationships: {
          variant: {
            data: {
              type: "catalog-variant",
              id: `$shopify:::$default:::${this.variantInput.value}`,
            },
          },
        },
      },
    };
  }

  /**
   * Opens the subscription dialog.
   */
  openDialog() {
    this.dialog.showModal();
  }

  /**
   * Closes the dialog when a close button is clicked.
   * @param {Event} event - Click event from the dialog.
   */
  closeDialog(event) {
    const btn = event.target.closest("button");
    if (!btn) return;
    this.dialog.close();
  }
}
customElements.define(
  "klaviyo-back-in-stock-subscription",
  KlaviyoBackInStockSubscription
);
